import { toast } from "@devhop/ui";
import type {
  BulkDeleteDTO,
  BulkToggleStatusDTO,
  CheckActivePackageResponseDTO,
  CreatePackageTemplateDTO,
  PackageTemplateDTO,
  SellPackageDTO,
  SellPackageResponseDTO,
  LockPartnerProjectPricingDTO,
  PartnerProjectSaleDTO,
  PartnerProjectSaleListItemDTO,
  SellPartnerProjectDTO,
  SellPartnerProjectResponseDTO,
  UpdatePartnerProjectPricingDTO,
  UpdatePackageTemplateDTO,
} from "@/modules/packages/domain/contracts";
import type { BulkCreateConflictItem } from "@/modules/classes/domain/repo/sessions/bulk-create";
import type { PackageTemplateUsageStats } from "@/modules/packages/domain/repo/get-usage-stats";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher, handleError } from "@/shared/lib/fetcher";
import { SellPartnerScheduleHttpError } from "./sell-partner-schedule-http-error";

export const packagesApi = {
  templates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PackageTemplateDTO & { rulesCount: number }>> {
      const url = new URL(`${config.apiUrl}/packages/templates`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<
        OffsetPageDTO<PackageTemplateDTO & { rulesCount: number }>
      >(url.toString());
    },
    async get(id: string): Promise<PackageTemplateDTO> {
      return fetcher.get<PackageTemplateDTO>(
        `${config.apiUrl}/packages/templates/${id}`,
      );
    },
    async getUsageStats(id: string): Promise<PackageTemplateUsageStats> {
      return fetcher.get<PackageTemplateUsageStats>(
        `${config.apiUrl}/packages/templates/${id}/usage-stats`,
      );
    },
    async create(input: CreatePackageTemplateDTO): Promise<{ id: string }> {
      return fetcher.post<{ id: string }>(
        `${config.apiUrl}/packages/templates`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdatePackageTemplateDTO,
    ): Promise<{ id: string }> {
      return fetcher.put<{ id: string }>(
        `${config.apiUrl}/packages/templates/${id}`,
        input,
      );
    },
    async toggleStatus(
      id: string,
      status: "active" | "inactive",
    ): Promise<{ toggled: boolean }> {
      return fetcher.post<{ toggled: boolean }>(
        `${config.apiUrl}/packages/templates/${id}/toggle-status`,
        { status },
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/packages/templates/${id}`,
      );
    },
    async bulkDelete(input: BulkDeleteDTO): Promise<{ deletedCount: number }> {
      return fetcher.post<{ deletedCount: number }>(
        `${config.apiUrl}/packages/templates/bulk-delete`,
        input,
      );
    },
    async bulkToggleStatus(
      input: BulkToggleStatusDTO,
    ): Promise<{ updatedCount: number }> {
      return fetcher.post<{ updatedCount: number }>(
        `${config.apiUrl}/packages/templates/bulk-toggle-status`,
        input,
      );
    },
  },
  sell: {
    async checkActivePackage(
      studentId: string,
    ): Promise<CheckActivePackageResponseDTO> {
      return fetcher.get<CheckActivePackageResponseDTO>(
        `${config.apiUrl}/packages/sell/check-active-package/${studentId}`,
      );
    },
    async sellPackage(input: SellPackageDTO): Promise<SellPackageResponseDTO> {
      return fetcher.post<SellPackageResponseDTO>(
        `${config.apiUrl}/packages/sell`,
        input,
      );
    },
    async sellPartnerProject(
      input: SellPartnerProjectDTO,
    ): Promise<SellPartnerProjectResponseDTO> {
      const url = `${config.apiUrl}/packages/sell/partner-project`;
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
          credentials: "include",
        });
        if (!res.ok) {
          const parsed: unknown = await res.clone().json().catch(() => null);
          const rec =
            parsed && typeof parsed === "object"
              ? (parsed as Record<string, unknown>)
              : null;
          if (
            res.status === 409 &&
            rec?.error === "PARTNER_SCHEDULE_CONFLICT" &&
            Array.isArray(rec.conflictingDates)
          ) {
            throw new SellPartnerScheduleHttpError(
              rec.conflictingDates as BulkCreateConflictItem[],
            );
          }
          if (
            res.status === 400 &&
            (rec?.error === "INSTALLMENT_TOTAL_MISMATCH" ||
              rec?.error === "INSTALLMENTS_REQUIRED")
          ) {
            throw {
              error: rec.error,
              message:
                typeof rec.message === "string"
                  ? rec.message
                  : "ແຜນງວດຊຳລະບໍ່ຖືກຕ້ອງ",
            };
          }
          await handleError(res);
        }
        return (await res.json()) as SellPartnerProjectResponseDTO;
      } catch (e) {
        if (e instanceof SellPartnerScheduleHttpError) throw e;
        const errRec =
          e && typeof e === "object" ? (e as Record<string, unknown>) : null;
        if (
          errRec?.error === "INSTALLMENT_TOTAL_MISMATCH" ||
          errRec?.error === "INSTALLMENTS_REQUIRED"
        ) {
          throw e;
        }
        toast.error("ເຄືອຂ່າຍມີບັນຫາ", {
          description: "ກະລຸນາກວດກາໄຟອິນເຕີເນັດຂອງທ່ານ.",
        });
        throw e;
      }
    },
  },
  studentPackages: {
    async cancel(
      id: string,
      body: { cancelReason: string },
    ): Promise<{ success: boolean }> {
      return fetcher.put<{ success: boolean }>(
        `${config.apiUrl}/packages/student-packages/${id}/cancel`,
        body,
      );
    },
  },

  partnerProjectSales: {
    async listPendingLock(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PartnerProjectSaleListItemDTO>> {
      const params = new URLSearchParams();
      params.set("limit", String(query.limit ?? 20));
      params.set("offset", String(query.offset ?? 0));
      if (query.sort?.length) {
        params.set("sort", JSON.stringify(query.sort));
      }
      if (query.filters?.length) {
        params.set("filters", JSON.stringify(query.filters));
      }
      return fetcher.get<OffsetPageDTO<PartnerProjectSaleListItemDTO>>(
        `${config.apiUrl}/packages/partner-project-sales/pending-lock?${params.toString()}`,
      );
    },

    async get(id: string): Promise<PartnerProjectSaleDTO> {
      return fetcher.get<PartnerProjectSaleDTO>(
        `${config.apiUrl}/packages/partner-project-sales/${id}`,
      );
    },

    async updatePricing(
      id: string,
      input: UpdatePartnerProjectPricingDTO,
    ): Promise<PartnerProjectSaleDTO> {
      return fetcher.patch<PartnerProjectSaleDTO>(
        `${config.apiUrl}/packages/partner-project-sales/${id}/pricing`,
        input,
      );
    },

    async lockPricing(
      id: string,
      input: LockPartnerProjectPricingDTO,
    ): Promise<SellPartnerProjectResponseDTO> {
      return fetcher.post<SellPartnerProjectResponseDTO>(
        `${config.apiUrl}/packages/partner-project-sales/${id}/lock-pricing`,
        input,
      );
    },
  },
};
